/**
 * Envelopes API (TMMAPP-0005 / TMMAPP-0011, design §7.6–§7.7, §8.5) — wire types from
 * `src/api/generated/`; normalized into the module's domain type. Money is integer minor units
 * in the virtual account OWNER's currency; progress is basis points and may exceed 10000 (MM-D-41).
 */
import { normalizeCategories, type Category } from './categories';
import { download, request, type DownloadResult } from './client';
import type {
  AllTimeCategoryTotals as WireAllTimeCategoryTotals,
  CategoryTotalItem as WireCategoryTotalItem,
  CategoryTotals as WireCategoryTotals,
  MoveRequest,
  MoveResponse,
  EnvelopeCreateRequest as WireEnvelopeCreateRequest,
  EnvelopeBudgetEntry as WireBudgetEntry,
  EnvelopePatchRequest as WireEnvelopePatchRequest,
  EnvelopeReorderRequest,
  EnvelopeResponse as WireEnvelope,
  PeriodDefault as WirePeriodDefault,
  PeriodFigures as WirePeriodFigures,
} from './generated';

export type EnvelopeKind = 'CASH' | 'BUDGET' | 'SAVINGS';
export type PeriodKind = 'CALENDAR_YEAR' | 'FISCAL_YEAR' | 'ROLLING_MONTHS' | 'ALL_TIME';

export interface PeriodDefault {
  periodKind: PeriodKind;
  fiscalYearStartMonth: number | null;
  rollingMonths: number | null;
}

/** One month-end balance. `month` is a civil `YYYY-MM`. */
export interface BalancePoint {
  month: string;
  balanceMinor: number;
}

export interface Envelope {
  aiAutoApplyBp: number | null;
  id: number;
  name: string;
  kind: EnvelopeKind;
  balanceMinor: number | null;
  targetMinor: number | null;
  targetOn: string | null;
  progressBp: number | null;
  periodDefault: PeriodDefault;
  sortOrder: number | null;
  archivedAt: string | null;
  /** Month-end balances, oldest first. */
  balanceSeries: BalancePoint[];
  /** Top categories over the envelope's default period. */
  categoryTotals: CategoryTotals;
  /** Latest allocation instant. Null when none. */
  lastActivityAt: string | null;
  /** Period rollup. Served on envelope detail; null on a list row. */
  periodFigures: PeriodFigures | null;
  categories: Category[] | null;
  referenceAprBp: number | null;
  notes: string | null;
  aiAnalysisAllowed: boolean;
  /** Every category's all-time total, ordered by |amount| (TMMAPI-0143, published). Null when absent. */
  allTimeCategoryTotals: AllTimeCategoryTotals | null;
  /** All-time in / out (TMMAPI-0143, published). Null when absent. */
  allTimeFlows: AllTimeFlows | null;
}

export interface CategoryTotalItem {
  categoryId: number;
  name: string;
  kind: 'INCOME' | 'EXPENSE';
  amountMinor: number;
}

export interface CategoryTotals {
  periodFrom: string;
  periodTo: string;
  items: CategoryTotalItem[];
  otherMinor: number;
  uncategorizedMinor: number;
}

/** A category id with no category row is counted in `uncategorizedMinor`. */
export interface AllTimeCategoryTotals {
  items: CategoryTotalItem[];
  uncategorizedMinor: number;
}

export interface AllTimeFlows {
  inMinor: number;
  outMinor: number;
}

export interface PeriodFigures {
  periodFrom: string;
  periodTo: string;
  inMinor: number;
  outMinor: number;
  spentMinor: number | null;
  remainingMinor: number | null;
  toGoMinor: number | null;
}

export interface EnvelopeCreateRequest {
  name: string;
  kind: EnvelopeKind;
  targetMinor?: number;
  targetOn?: string;
  periodKind?: PeriodKind;
  fiscalYearStartMonth?: number;
  rollingMonths?: number;
  sortOrder?: number;
  referenceAprBp?: number;
  notes?: string;
}

/**
 * Only the keys the person changed; every other key is omitted (unchanged). `aiAutoApplyBp` is
 * three-state: `null` clears it. `targetEffectiveFrom` (MM-D-118) is the civil date `targetMinor`
 * takes effect from; past or future; it needs `targetMinor`.
 */
export type EnvelopePatchRequest = WireEnvelopePatchRequest;

/** One dated budget (MM-D-118): newest first; "who · when" is the change's author and instant. */
export interface EnvelopeBudget {
  effectiveFrom: string;
  /** `null`: no budget from that date (a budget cleared). */
  targetMinor: number | null;
  createdByName: string | null;
  createdAt: string;
}

export function normalizeEnvelopeBudget(w: WireBudgetEntry): EnvelopeBudget {
  return {
    effectiveFrom: w.effectiveFrom,
    targetMinor: w.targetMinor,
    createdByName: w.createdByName ?? null,
    createdAt: w.createdAt,
  };
}

export interface MoveResult {
  audienceBefore: string[];
  audienceAfter: string[];
}

const ENVELOPE_KINDS: ReadonlySet<string> = new Set(['CASH', 'BUDGET', 'SAVINGS']);
const PERIOD_KINDS: ReadonlySet<string> = new Set([
  'CALENDAR_YEAR',
  'FISCAL_YEAR',
  'ROLLING_MONTHS',
  'ALL_TIME',
]);

function normalizePeriodDefault(w: WirePeriodDefault): PeriodDefault {
  // MM-D-40: an unknown future period kind reads as the calendar year.
  const periodKind = PERIOD_KINDS.has(w.periodKind) ? w.periodKind : 'CALENDAR_YEAR';
  return {
    periodKind,
    fiscalYearStartMonth: w.fiscalYearStartMonth,
    rollingMonths: w.rollingMonths,
  };
}

const CATEGORY_KINDS: ReadonlySet<string> = new Set(['INCOME', 'EXPENSE']);

/** The category rows of a rollup; a row with an unknown kind is dropped. */
function normalizeCategoryTotalItems(items: readonly WireCategoryTotalItem[]): CategoryTotalItem[] {
  return items.flatMap((item) =>
    CATEGORY_KINDS.has(item.kind)
      ? [
          {
            categoryId: item.categoryId,
            name: item.name,
            kind: item.kind,
            amountMinor: item.amountMinor,
          },
        ]
      : [],
  );
}

/** Top-category rollup over the envelope's default period. */
function normalizeCategoryTotals(w: WireCategoryTotals): CategoryTotals {
  return {
    periodFrom: w.periodFrom,
    periodTo: w.periodTo,
    items: normalizeCategoryTotalItems(w.items),
    otherMinor: w.otherMinor,
    uncategorizedMinor: w.uncategorizedMinor,
  };
}

/** All-time category rollup (TMMAPI-0143). */
function normalizeAllTimeCategoryTotals(w: WireAllTimeCategoryTotals): AllTimeCategoryTotals {
  return { items: normalizeCategoryTotalItems(w.items), uncategorizedMinor: w.uncategorizedMinor };
}

/** Detail rollup; the spent / remaining / to-go figures are optional. */
function normalizePeriodFigures(w: WirePeriodFigures): PeriodFigures {
  return {
    periodFrom: w.periodFrom,
    periodTo: w.periodTo,
    inMinor: w.inMinor,
    outMinor: w.outMinor,
    spentMinor: w.spentMinor ?? null,
    remainingMinor: w.remainingMinor ?? null,
    toGoMinor: w.toGoMinor ?? null,
  };
}

/** An envelope row; `null` (dropped, warned in DEV) with an unknown kind. */
export function normalizeEnvelope(w: WireEnvelope): Envelope | null {
  if (!ENVELOPE_KINDS.has(w.kind)) {
    if (import.meta.env.DEV) console.warn('envelopes: unknown kind dropped', w.kind);
    return null;
  }
  return {
    id: w.id,
    name: w.name,
    kind: w.kind,
    balanceMinor: w.balanceMinor ?? null,
    targetMinor: w.targetMinor ?? null,
    targetOn: w.targetOn ?? null,
    progressBp: w.progressBp ?? null,
    periodDefault: normalizePeriodDefault(w.periodDefault),
    sortOrder: w.sortOrder ?? null,
    archivedAt: w.archivedAt ?? null,
    balanceSeries: w.balanceSeries.map((point) => ({
      month: point.month,
      balanceMinor: point.balanceMinor,
    })),
    categoryTotals: normalizeCategoryTotals(w.categoryTotals),
    lastActivityAt: w.lastActivityAt,
    periodFigures: w.periodFigures == null ? null : normalizePeriodFigures(w.periodFigures),
    categories: w.categories == null ? null : normalizeCategories(w.categories),
    referenceAprBp: w.referenceAprBp ?? null,
    notes: w.notes ?? null,
    aiAnalysisAllowed: w.aiAnalysisAllowed,
    aiAutoApplyBp: w.aiAutoApplyBp ?? null,
    allTimeCategoryTotals:
      w.allTimeCategoryTotals == null
        ? null
        : normalizeAllTimeCategoryTotals(w.allTimeCategoryTotals),
    allTimeFlows:
      w.allTimeFlows == null
        ? null
        : { inMinor: w.allTimeFlows.inMinor, outMinor: w.allTimeFlows.outMinor },
  };
}

export function normalizeEnvelopes(value: readonly WireEnvelope[]): Envelope[] {
  return value.flatMap((row) => {
    const envelope = normalizeEnvelope(row);
    return envelope ? [envelope] : [];
  });
}

function required(wire: WireEnvelope): Envelope {
  const envelope = normalizeEnvelope(wire);
  if (!envelope) throw new Error('envelope kind is unknown');
  return envelope;
}

export const envelopesApi = {
  /** GET /v1/virtual-accounts/{id}/envelopes — the server orders by `sortOrder`, then name. */
  async listForVirtualAccount(
    virtualAccountId: number,
    opts: { includeArchived?: boolean } = {},
  ): Promise<Envelope[]> {
    const path = `/v1/virtual-accounts/${virtualAccountId}/envelopes`;
    const wire = opts.includeArchived
      ? await request<WireEnvelope[]>('GET', path, { query: { includeArchived: true } })
      : await request<WireEnvelope[]>('GET', path);
    return normalizeEnvelopes(wire);
  },
  /** POST /v1/virtual-accounts/{id}/envelopes. Unset optional keys are omitted (the server's default). */
  async create(virtualAccountId: number, body: EnvelopeCreateRequest): Promise<Envelope> {
    const json: WireEnvelopeCreateRequest = body;
    return required(
      await request<WireEnvelope>('POST', `/v1/virtual-accounts/${virtualAccountId}/envelopes`, {
        json,
      }),
    );
  },
  /** GET /v1/envelopes/{envelopeId}. */
  async get(envelopeId: number): Promise<Envelope> {
    return required(await request<WireEnvelope>('GET', `/v1/envelopes/${envelopeId}`));
  },
  /** PATCH /v1/envelopes/{envelopeId} — only the changed keys; `null` clears `aiAutoApplyBp`. */
  async update(envelopeId: number, body: EnvelopePatchRequest): Promise<Envelope> {
    return required(
      await request<WireEnvelope>('PATCH', `/v1/envelopes/${envelopeId}`, { json: body }),
    );
  },
  /** GET /v1/envelopes/{envelopeId}/budgets — the dated budgets, newest first (MM-D-118). */
  async budgets(envelopeId: number): Promise<EnvelopeBudget[]> {
    const wire = await request<WireBudgetEntry[]>('GET', `/v1/envelopes/${envelopeId}/budgets`);
    return wire.map(normalizeEnvelopeBudget);
  },
  /** POST /v1/envelopes/{envelopeId}/archive. */
  async archive(envelopeId: number): Promise<void> {
    await request<unknown>('POST', `/v1/envelopes/${envelopeId}/archive`);
  },
  /** POST /v1/envelopes/{envelopeId}/unarchive. */
  async unarchive(envelopeId: number): Promise<void> {
    await request<unknown>('POST', `/v1/envelopes/${envelopeId}/unarchive`);
  },
  /** POST /v1/virtual-accounts/{id}/envelopes/reorder — the active envelopes. */
  async reorder(virtualAccountId: number, envelopeIds: number[]): Promise<Envelope[]> {
    return normalizeEnvelopes(
      await request<WireEnvelope[]>(
        'POST',
        `/v1/virtual-accounts/${virtualAccountId}/envelopes/reorder`,
        {
          json: { envelopeIds } satisfies EnvelopeReorderRequest,
        },
      ),
    );
  },
  /** POST /v1/envelopes/{envelopeId}/move — the audience before / after, by display name. */
  async move(envelopeId: number, virtualAccountId: number): Promise<MoveResult> {
    const wire = await request<MoveResponse>('POST', `/v1/envelopes/${envelopeId}/move`, {
      json: { virtualAccountId } satisfies MoveRequest,
    });
    return { audienceBefore: wire.audienceBefore, audienceAfter: wire.audienceAfter };
  },
  /** GET /v1/envelopes/{envelopeId}/export.xlsx — the overview workbook. */
  async exportXlsx(
    envelopeId: number,
    window: { from?: string | undefined; to?: string | undefined } = {},
  ): Promise<DownloadResult> {
    return download(`/v1/envelopes/${envelopeId}/export.xlsx`, {
      query: { from: window.from, to: window.to },
    });
  },
};
